// @ts-check

import React from 'react';
import { array, func, object } from 'prop-types';
import USER from '../ts/profileEditorEnum';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { styled, lighten, darken } from '@mui/system';
import { filterRoleGroupsOptions } from '../libs/filterRoleGroupsOptions';
import { getOptionLabel, makeGroupByAppId } from '../libs/filterRoleGroupsOptions';

/**
 * @typedef {Object} GroupHeaderProps
 * @prop {import('@mui/material/styles').Theme} theme - The theme object containing styling
 */

/**
 * @component RoleGroupSelector
 * @param {Object} props
 * @param {RoleGroupType[]} props.values
 * @param {RoleGroupType[]} props.options
 * @param {EditorValidType} props.valid
 * @param {UserInfoType} props.userInfo
 * @param {(name:string, payload:any) => void} props.onHandleUser
 * @returns {JSX.Element}
 */
function RoleGroupSelector({ values, options, valid, userInfo, onHandleUser }) {
	/**
	 * @component GroupHeader
	 * @param {GroupHeaderProps} props - The component props.
	 * @returns {JSX.Element} The styled 'div' component acting as a group header.
	 */
	const GroupHeader = styled('div')((props) => {
		const { theme } = props;
		return {
			position: 'sticky',
			top: '-8px',
			padding: '4px 10px',
			color: theme.palette.primary.main,
			backgroundColor:
				theme.palette.mode === 'light'
					? lighten(theme.palette.primary.light, 0.85)
					: darken(theme.palette.primary.main, 0.8),
		};
	});

	/**
	 * @component GroupItems
	 * @param {Object} styles
	 * @param {number|string} styles.padding
	 * @returns {JSX.Element}
	 */
	const GroupItems = styled('ul')({ padding: 0 });

	return (
		<Autocomplete
			fullWidth
			multiple
			value={values}
			options={filterRoleGroupsOptions(options, userInfo.userRoleGroups)}
			groupBy={makeGroupByAppId}
			getOptionLabel={getOptionLabel}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			onChange={(e, newValue) => onHandleUser(USER.ROLE_GROUPS, newValue)}
			renderGroup={(params) => {
				return (
					<li key={params.key}>
						<GroupHeader>{params.group}</GroupHeader>
						<GroupItems>{params.children}</GroupItems>
					</li>
				);
			}}
			renderInput={(params) => {
				return (
					<TextField
						{...params}
						variant="outlined"
						label="Role Groups"
						required
						InputLabelProps={{ shrink: true }}
						size="small"
						error={valid.hasErr}
						helperText={valid.msg}
					/>
				);
			}}
		/>
	);
}

RoleGroupSelector.propTypes = {
	values: array,
	options: array,
	valid: object,
	userInfo: object,
	onHandleUser: func,
};

export default RoleGroupSelector;
